import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation } from "wouter";
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { apiClient } from "@/lib/api-client";
import { AppShell } from "@/components/layouts/AppShell";
import NewTicketInlineForm from "@/components/new-ticket-inline-form";
import { lazyWithRetry } from '@/lib/lazy-with-retry';
const EditTicketInlineModal = lazyWithRetry(() => import('@/components/edit-ticket-inline-modal'));
import { ZoomIn, ZoomOut, RotateCcw, Maximize2, X, Upload, Heart, Plus } from "lucide-react";
import { useWeatherStore } from '@/stores/weatherStore';
// Weather initialization removed - weather is now fetched on-demand when creating tickets/adding photos
import TicketChat from '@/components/ticket-chat';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAlert } from "@/hooks/use-alert";

// Import extracted components
import { ProjectDocumentsTab } from './project-details/components/ProjectDocumentsTab';
import { ProjectDetailsTab } from './project-details/components/ProjectDetailsTab';
const ProjectPlanLevelTab = lazyWithRetry(() => import('./project-details/components/ProjectPlanLevelTab').then(m => ({ default: m.ProjectPlanLevelTab })));
// PDF viewer is loaded inside the lazily loaded Plan tab
// TicketExportButton moved to ProjectTicketsTab sidebar
import type { TicketExportDataTicketsTab } from './project-details/services/TicketPDFExportServiceTicketsTab';
import { useDocumentActions } from './project-details/hooks/useDocumentActions';
import { useUploadPlanMutation } from './project-details/hooks/useUploadPlanMutation';
// Lazy-load PlanImageCaptureService only when needed
let PlanImageCaptureService: any;
async function ensurePlanImageCaptureService() {
  if (!PlanImageCaptureService) {
    PlanImageCaptureService = await import('./project-details/services/PlanImageCaptureService').then(m => m.PlanImageCaptureService);
  }
  return PlanImageCaptureService;
}
import { ProjectTeamTab } from './project-details/components/ProjectTeamTab';
import { GroupsTab } from './project-details/components/GroupsTab';
import { ProjectTicketsTab } from './project-details/components/ProjectTicketsTab';
import { useCurrentOrganization } from '@/hooks/use-current-organization';
import { useOrganizationSelector } from '@/hooks/use-organization-selector';
import { usePermissions, Permission } from '@/hooks/use-permissions';
import { useProjectCurrency } from '@/hooks/use-project-currency';
import { useOrganizationCurrency } from '@/hooks/use-organization-currency';
import { useProjectStatuses, resolveProjectStatusIdForProgress } from '@/hooks/use-project-statuses';
import { useClientTypes } from '@/hooks/use-client-types';
import { RequirePermission } from '@/components/RequirePermission';
import { getStatusColor } from '@/utils/color-utils';
import { usePriorities } from '@/hooks/use-priorities';
import { QUERY_KEYS } from './project-details/utils/project-constants';
import { useDateFormat } from '@/utils/date-formatter';
import { LogoCropModal } from '@/components/LogoCropModal';
import { LogoLoader } from '@/components/ui/logo-loader';
import { PageHeaderLayout } from '@/components/layouts/PageHeaderLayout';
import { HeaderSearchInput, HeaderTabNav } from '@/components/layouts/header-slots';
import { TicketSummaryCards, type SemanticType } from '@/components/ticket-summary-cards';
import { ProjectHeaderSearch, ProjectHeaderTeamSearch } from './project-details/components/ProjectHeader';
import { trackEvent } from '@/lib/analytics';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@mangelapp/shared';
import { projectDateToInputValue } from '@/utils/project-dates';
import { ticketDueDateToInputValue as toDateInputValue } from '@/utils/ticket-calendar-date';

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const [location, setLocation] = useLocation();
  const { t, i18n } = useTranslation(['pages.projectDetails', 'components.ticket', 'common', 'tickets', 'pages.projects']);
  const queryClient = useQueryClient();
  const { organizationId } = useCurrentOrganization();

  // GA4: project_opened when viewing a project
  useEffect(() => {
    if (id && organizationId) {
      trackEvent(ANALYTICS_EVENTS.PROJECT_OPENED, {
        [ANALYTICS_PARAMS.PROJECT_ID]: id,
        [ANALYTICS_PARAMS.ORGANIZATION_ID]: organizationId,
      });
    }
  }, [id, organizationId]);
  const currency = useProjectCurrency(id);
  const orgCurrency = useOrganizationCurrency();
  const { formatDate } = useDateFormat();
  const { alert, confirm } = useAlert();

  // Check if user is contractor in the selected organization
  const { isContractor } = useOrganizationSelector();

  // Check permissions
  const { canDeleteTickets: canDeleteTicketsFromHook, hasPermission, canManageProjectSettings, canCreateTickets } = usePermissions();
  const canEditProjectDetails = hasPermission('manage_projects') || hasPermission('manage_all_projects');
  const canManageTeamMembers = hasPermission('manage_projects') || hasPermission('manage_all_projects');
  const canManageGroups = hasPermission('manage_projects') || hasPermission('manage_all_projects');
  const canManagePlans = hasPermission('manage_projects') || hasPermission('manage_all_projects');
  const canManageDocuments = hasPermission('manage_projects') || hasPermission('manage_all_projects');

  // Ticket delete/edit follows project manage scope.
  const canDeleteTickets = () => {
    return canDeleteTicketsFromHook() || hasPermission('manage_projects') || hasPermission('manage_all_projects');
  };

  // Weather initialization removed - weather is now fetched on-demand when creating tickets/adding photos
  // getProjectWeather is still used for legacy display purposes but weather is fetched directly via API when needed
  const { getProjectWeather } = useWeatherStore();

  // URL parameter processing
  const getSearchParamsFromLocation = useCallback(() => {
    if (typeof window === 'undefined') {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }, []);

  const locationSearchParams = getSearchParamsFromLocation();
  const tabParam = locationSearchParams.get('tab');
  const editTicketParam = locationSearchParams.get('editTicket');
  const inlineEditParam = locationSearchParams.get('inlineEdit');
  const ticketIdParam = locationSearchParams.get('ticketId');

  // State management
  const [activeTab, setActiveTab] = useState(tabParam || "details");

  // Search state
  const [globalTicketSearchTerm, setGlobalTicketSearchTerm] = useState('');
  const [globalTeamSearchTerm, setGlobalTeamSearchTerm] = useState('');
  const [globalGroupSearchTerm, setGlobalGroupSearchTerm] = useState('');
  const [globalDocumentsSearchTerm, setGlobalDocumentsSearchTerm] = useState('');
  const [detailsSearchTerm, setDetailsSearchTerm] = useState('');

  // Editable project fields - contractors can view but not edit
  const [isEditing, setIsEditing] = useState(!isContractor);
  const [editedProject, setEditedProject] = useState<any>({});

  // Inline editing states for frequently changed fields
  const [isEditingStartDate, setIsEditingStartDate] = useState(false);
  const [isEditingEndDate, setIsEditingEndDate] = useState(false);

  // Plan Level tab state
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const hasRestoredPlanRef = useRef<boolean>(false);
  const previousSelectedPlanRef = useRef<any>(null);
  const previousPlanIdsRef = useRef<Set<string>>(new Set());
  // Ticket detail view state
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [showInlineEditModal, setShowInlineEditModal] = useState(false);
  const [detailPlanImage, setDetailPlanImage] = useState<string | null>(null);
  const [detailPlanPin, setDetailPlanPin] = useState<{ x: number; y: number } | null>(null);

  // Ticket form state
  const [ticketForm, setTicketForm] = useState({
    title: '',
    description: '',
    status: '',
    priority: '',
    trade: '',
    type: '',
    location: '',
    cost: '',
    assignedTo: '',
    dueDate: '', // Fällig bis
    extensionDate: '', // Nachfrist bis
    progress: 0,
    createdAt: '' // Created at field
  });

  // Ticket images state - using images array
  const [ticketImages, setTicketImages] = useState<string[]>([]);

  const documentsUploadHandlerRef = useRef<(() => void) | null>(null);
  const planUploadHandlerRef = useRef<(() => void) | null>(null);
  const planFileInputRef = useRef<HTMLInputElement>(null);
  const subscriptionHiddenRedirectRef = useRef(false);

  useEffect(() => {
    subscriptionHiddenRedirectRef.current = false;
  }, [id]);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<any>(null);

  // Project delete confirmation state

  const { toast } = useToast();

  // New ticket modal state
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);

  // Plan upload state
  const [showPlanUpload, setShowPlanUpload] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planFile, setPlanFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRefreshingPlans, setIsRefreshingPlans] = useState(false);

  // Plan level middle controls state
  const [planLevelMiddleControls, setPlanLevelMiddleControls] = useState<React.ReactNode>(null);
  // Plan level start pin placement function - use ref to avoid setState during render
  const startPinPlacementRef = useRef<(() => void) | null>(null);
  const openAddMemberRef = useRef<(() => void) | null>(null);
  const deleteSelectedTeamMembersRef = useRef<(() => void) | null>(null);
  const openCreateGroupRef = useRef<(() => void) | null>(null);
  const [teamSelectionCount, setTeamSelectionCount] = useState(0);
  const deleteSelectedGroupsRef = useRef<(() => void) | null>(null);
  const [groupSelectionCount, setGroupSelectionCount] = useState(0);

  // Tickets tab selection state (for header Export button)
  const [ticketsTabHasSelection, setTicketsTabHasSelection] = useState(false);
  const ticketsTabGetSelectedDataRef = useRef<(() => any[]) | null>(null);
  // Tickets tab plan level filter ('' = all plans, plan id = filter by that plan)
  const [ticketsTabPlanLevelId, setTicketsTabPlanLevelId] = useState('');

  const updateTabInUrl = useCallback((tabId: string) => {
    const [path] = location.split('?');
    const params = getSearchParamsFromLocation();
    params.set('tab', tabId);
    const serializedSearch = params.toString();
    const nextLocation = serializedSearch ? `${path}?${serializedSearch}` : path;
    if (nextLocation !== location) {
      setLocation(nextLocation, { replace: true });
    }
  }, [getSearchParamsFromLocation, location, setLocation]);

  const handleTabChange = useCallback(
    (
      tabId: string,
      options?: {
        keepPlanLevelControls?: boolean;
        keepStartPinPlacement?: boolean;
      }
    ) => {
      setActiveTab(tabId);
      updateTabInUrl(tabId);
      if (!options?.keepPlanLevelControls) {
        setPlanLevelMiddleControls(null);
      }
      if (!options?.keepStartPinPlacement) {
        startPinPlacementRef.current = null;
      }
    },
    [updateTabInUrl]
  );

  useEffect(() => {
    const params = getSearchParamsFromLocation();
    const tabFromUrl = params.get('tab');

    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [activeTab, getSearchParamsFromLocation, location]);

  // Plan editing state
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingPlanName, setEditingPlanName] = useState('');
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);

  // Ticket creation state
  const [pendingPinLocation, setPendingPinLocation] = useState<{ x: number, y: number, planId: string, zoom: number } | null>(null);

  // Form fields
  const [projektNummer, setProjektNummer] = useState('');
  const [projektName, setProjektName] = useState('');
  const [street, setStreet] = useState('');
  const [houseNo, setHouseNo] = useState('');
  const [addressSupplement, setAddressSupplement] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [auftraggeber, setAuftraggeber] = useState('');
  const [clientTypeId, setClientTypeId] = useState<string | null>(null);
  const [currencyValue, setCurrencyValue] = useState('');
  const [projektleiter, setProjektleiter] = useState('');
  const [projektleiterId, setProjektleiterId] = useState<string | null>(null);
  const [managerTeamEntryId, setManagerTeamEntryId] = useState<string | null>(null);
  const [projectStatusId, setProjectStatusId] = useState<string | null>(null);
  const [startDatum, setStartDatum] = useState('');
  const [endDatum, setEndDatum] = useState('');
  const [fortschritt, setFortschritt] = useState(0);
  const [coordinateWarning, setCoordinateWarning] = useState<string | undefined>();
  const [weatherValidationError, setWeatherValidationError] = useState<string | null>(null);
  const [isValidatingWeather, setIsValidatingWeather] = useState(false);
  const justSavedCurrencyRef = useRef<string | null>(null);

  // Store initial project values to track form changes
  const [initialProjectValues, setInitialProjectValues] = useState<{
    projektNummer: string;
    projektName: string;
    street: string;
    houseNo: string;
    addressSupplement: string;
    city: string;
    postalCode: string;
    country: string;
    latitude: number | null;
    longitude: number | null;
    auftraggeber: string;
    clientTypeId: string | null;
    currency: string;
    projektleiter: string;
    projektleiterId: string | null;
    managerTeamEntryId: string | null;
    projectStatusId: string | null;
    startDatum: string;
    endDatum: string;
    fortschritt: number;
  } | null>(null);

  // Debounce timer for slider changes
  const [sliderTimeout, setSliderTimeout] = useState<NodeJS.Timeout | null>(null);

  // Load project statuses to get Completed status ID
  const { data: projectStatuses = [] } = useProjectStatuses();

  // Load client types for Excel export
  const { data: clientTypes = [] } = useClientTypes();

  // Debounced save function
  const debouncedSave = (newProgress: number) => {
    if (sliderTimeout) {
      clearTimeout(sliderTimeout);
    }

    const timeout = setTimeout(async () => {
      const newProjectStatusId = resolveProjectStatusIdForProgress(
        newProgress,
        projectStatusId,
        projectStatuses,
      );

      try {
        const updateData: any = {
          fortschritt: newProgress,
        };
        if (newProjectStatusId !== projectStatusId) {
          updateData.projectStatusId = newProjectStatusId;
        }

        const response = await apiRequest(`/api/projects/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          const result = await response.json();
          const updatedProject = result?.data || {};
          const projectPatch = {
            fortschritt: newProgress,
            ...(newProjectStatusId !== projectStatusId ? { projectStatusId: newProjectStatusId } : {}),
            ...updatedProject,
          };

          updateProjectCache(projectPatch);
          updateProjectListCaches(projectPatch);

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["/api/projects"] }),
            queryClient.invalidateQueries({ queryKey: ["/api/projects", organizationId] }),
            queryClient.invalidateQueries({ queryKey: ["/api/stats"] }),
            queryClient.invalidateQueries({ queryKey: ["/api/stats", organizationId] }),
          ]);

          if (newProjectStatusId !== projectStatusId) {
            setProjectStatusId(newProjectStatusId);
          }
        }
      } catch (error) {
        console.error('❌ Failed to save progress:', error);
      }
    }, 1000);

    setSliderTimeout(timeout);
  };

  // Fetch project data
  const { data: projectResponse, error } = useQuery({
    queryKey: ["/api/projects", id, "light"],
    queryFn: async () => {
      const response = await apiRequest(`/api/projects/${id}?light=1`);
      return await response.json();
    },
    enabled: !!id,
    retry: false,
  });

  const project = projectResponse?.data;

  useEffect(() => {
    if (!error) return;
    const code = (error as Error & { code?: string }).code;
    if (code !== 'PROJECTS_SUBSCRIPTION_HIDDEN') return;
    if (subscriptionHiddenRedirectRef.current) return;
    subscriptionHiddenRedirectRef.current = true;
    toast({
      title: t('pages.projects:subscriptionHidden.redirectToastTitle'),
      description: t('pages.projects:subscriptionHidden.redirectToastDescription'),
    });
    setLocation('/projects');
  }, [error, toast, t, setLocation]);

  const optimisticLogoPreviewUrlRef = useRef<string | null>(null);

  const updateProjectCache = useCallback((patch: Record<string, any>) => {
    const applyPatch = (oldData: any) => {
      if (!oldData) {
        return {
          success: true,
          data: patch,
        };
      }

      return {
        ...oldData,
        data: {
          ...oldData.data,
          ...patch,
        },
      };
    };

    queryClient.setQueryData(["/api/projects", id], applyPatch);
    queryClient.setQueryData(["/api/projects", id, "light"], applyPatch);
  }, [id, queryClient]);

  const updateProjectListCaches = useCallback((patch: Record<string, any>) => {
    const allProjectQueries = queryClient.getQueriesData({
      queryKey: ["/api/projects"],
    });

    allProjectQueries.forEach(([queryKey]) => {
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData?.data || !Array.isArray(oldData.data)) {
          return oldData;
        }

        return {
          ...oldData,
          data: oldData.data.map((listProject: any) =>
            listProject?.id === id ? { ...listProject, ...patch } : listProject
          ),
        };
      });
    });
  }, [id, queryClient]);

  useEffect(() => {
    return () => {
      if (optimisticLogoPreviewUrlRef.current) {
        URL.revokeObjectURL(optimisticLogoPreviewUrlRef.current);
        optimisticLogoPreviewUrlRef.current = null;
      }
    };
  }, []);

  // Initialize form fields when project data loads
  useEffect(() => {
    if (project) {
      const initialValues = {
        projektNummer: project.projektNummer || '',
        projektName: project.name || '',
        street: project.street || project.strasse || '',
        houseNo: project.houseNo || '',
        addressSupplement: project.addressSupplement || '',
        city: project.city || project.stadt || '',
        postalCode: project.postalCode || project.postal_code || project.postleitzahl || '',
        country: project.country || '',
        latitude: project.latitude !== null && project.latitude !== undefined ? (typeof project.latitude === 'string' ? parseFloat(project.latitude) : project.latitude) : null,
        longitude: project.longitude !== null && project.longitude !== undefined ? (typeof project.longitude === 'string' ? parseFloat(project.longitude) : project.longitude) : null,
        auftraggeber: project.auftraggeber || '',
        clientTypeId: project.clientTypeId || null,
        currency: project.currency || orgCurrency || 'EUR',
        projektleiter: project.managerName || '',
        projektleiterId: project.managerId || null,
        managerTeamEntryId: project.managerTeamEntryId || null,
        projectStatusId: project.projectStatusId || null,
        startDatum: projectDateToInputValue(project.startDatum || project.startDate),
        endDatum: projectDateToInputValue(project.endDatum || project.endDate),
        fortschritt: project.fortschritt || 0,
      };

      // Store initial values
      setInitialProjectValues(initialValues);

      // Set form fields
      setProjektNummer(initialValues.projektNummer);
      setProjektName(initialValues.projektName);
      setStreet(initialValues.street);
      setHouseNo(initialValues.houseNo);
      setAddressSupplement(initialValues.addressSupplement);
      setCity(initialValues.city);
      setPostalCode(initialValues.postalCode);
      setCountry(initialValues.country);
      setLatitude(initialValues.latitude);
      setLongitude(initialValues.longitude);
      setAuftraggeber(initialValues.auftraggeber);
      setClientTypeId(initialValues.clientTypeId);
      // Only update currency if we didn't just save it (to prevent overwriting saved value)
      // Also check if the current currencyValue is different from what's in project data
      // If we just saved a currency and it matches what we have, don't reset it
      if (justSavedCurrencyRef.current === null) {
        // No recent save, update from project data
        setCurrencyValue(initialValues.currency);
      } else if (justSavedCurrencyRef.current !== initialValues.currency) {
        // We just saved a currency, but project data doesn't match - this means the save didn't persist
        // Keep our saved value and log a warning
        console.warn('⚠️ Currency mismatch: saved', justSavedCurrencyRef.current, 'but project has', initialValues.currency);
        // Don't reset - keep the saved value
      }
      // If justSavedCurrencyRef.current === initialValues.currency, we don't need to do anything
      setProjektleiter(initialValues.projektleiter);
      setProjektleiterId(initialValues.projektleiterId);
      setManagerTeamEntryId(initialValues.managerTeamEntryId);
      setProjectStatusId(initialValues.projectStatusId);
      setStartDatum(initialValues.startDatum);
      setEndDatum(initialValues.endDatum);
      setFortschritt(initialValues.fortschritt);
    }
  }, [project, orgCurrency]);

  // Fetch tickets data - Priority query, enabled immediately when projectId is available
  const { data: ticketsResponse, isLoading: ticketsLoading } = useQuery({
    queryKey: QUERY_KEYS.projectTickets(id),
    queryFn: async () => {
      const response = await apiRequest(`/api/tickets/project/${id}`);
      return await response.json();
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch documents data - Only when documents tab is active
  const { data: documentsResponse, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ["/api/documents/project", id],
    queryFn: async () => {
      const response = await apiRequest(`/api/documents/project/${id}?limit=200&offset=0`);
      return await response.json();
    },
    enabled: !!id && activeTab === 'documents',
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch plans data - Preload plans when project loads for better UX
  // Plans are needed for: plan level tab, ticket creation/editing with plan locations, plan previews
  const { data: plansResponse, isLoading: isLoadingPlans, isSuccess: isPlansSuccess, refetch: refetchPlans } = useQuery({
    queryKey: QUERY_KEYS.projectPlans(id),
    queryFn: async () => {
      const response = await apiRequest(`/api/plans/project/${id}?limit=200&offset=0`);
      return await response.json();
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch team members data (contractors) to resolve contractor names
  const { data: teamResponse } = useQuery({
    queryKey: ["/api/contractors"],
    queryFn: async () => {
      const response = await apiRequest("/api/contractors");
      return await response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch team groups once at project level and pass to ticket modal to avoid duplicate modal fetches
  const { data: teamGroupsResponse } = useQuery({
    queryKey: ["/api/team-groups", id],
    queryFn: async () => {
      const response = await apiRequest(`/api/team-groups?projectId=${id}`);
      return await response.json();
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch gewerke data - Always fetch for display validation
  const { data: gewerkeResponse } = useQuery({
    queryKey: ["/api/gewerke", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` : "/api/gewerke";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes - matches other list data
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch typen data - Always fetch for display validation
  const { data: typenResponse } = useQuery({
    queryKey: ["/api/typen", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/typen?organizationId=${encodeURIComponent(organizationId)}` : "/api/typen";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes - matches other list data
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  const typen = typenResponse?.data || [];

  // Get status from globally cached data (loaded at app initialization via useOrganizationMetadata)
  const { data: statusResponse } = useQuery({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes - matches useOrganizationMetadata
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Extract data from responses
  const tickets = ticketsResponse?.data || [];
  const documents = documentsResponse?.data || [];
  const plans = plansResponse?.data || [];
  const planLimits = (plansResponse as any)?.limits || { maxPdfPlans: 0, currentCount: 0 };

  // Keep selectedPlan in sync with the current plans list.
  // This fixes the case where a plan gets deleted via PlanModificationModal (or any other place)
  // but selectedPlan still points to the now-missing plan, leaving it visible in the selector/viewer.
  useEffect(() => {
    if (!id) return;

    // Only run once we have a stable server-backed plan list.
    if (isLoadingPlans || !isPlansSuccess || !plansResponse) return;

    const prevIds = previousPlanIdsRef.current;
    const currentIds = new Set<string>(plans.map((p: any) => String(p.id)));

    if (selectedPlan?.id) {
      const selectedId = String(selectedPlan.id);
      const existedBefore = prevIds.has(selectedId);
      const existsNow = currentIds.has(selectedId);

      // Only clear selection when a previously-present plan disappears (deleted).
      // This avoids clearing for temporary selections (e.g. optimistic upload fallback).
      if (existedBefore && !existsNow) {
        const storageKey = `selectedPlan_${id}`;
        localStorage.removeItem(storageKey);
        previousSelectedPlanRef.current = null;
        setSelectedPlan(null);
      } else if (existsNow) {
        // Keep the selected plan object up to date (rename, visibility, etc.)
        const currentPlan = plans.find((p: any) => String(p?.id) === selectedId);
        if (
          currentPlan &&
          (
            currentPlan.name !== selectedPlan.name ||
            String(currentPlan.updatedAt ?? '') !== String(selectedPlan.updatedAt ?? '') ||
            currentPlan.fileUrl !== selectedPlan.fileUrl
          )
        ) {
          setSelectedPlan(currentPlan);
        }
      }
    }

    previousPlanIdsRef.current = currentIds;
  }, [id, isLoadingPlans, isPlansSuccess, plansResponse, plans, selectedPlan?.id]);

  // Use mutation-only plan upload hook to avoid unnecessary plans/bauzeitenplan read queries
  const { uploadPlan, isUploading: isUploadingPlan } = useUploadPlanMutation({ projectId: id! });

  // Handle plan file upload
  const handlePlanFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const uploadResult = await uploadPlan(file);
        const uploadedPlan = uploadResult?.data;

        // Reset the input so the same file can be selected again
        if (planFileInputRef.current) {
          planFileInputRef.current.value = '';
        }

        // Wait for plans query to refetch, then select the newly uploaded plan
        if (uploadedPlan?.id) {
          // Refetch plans query to get the updated list
          const { data: refetchedPlansResponse } = await refetchPlans();
          const refetchedPlans = refetchedPlansResponse?.data || [];

          // Find and select the newly uploaded plan
          const newPlan = refetchedPlans.find((p: any) => p.id === uploadedPlan.id);

          if (newPlan) {
            setSelectedPlan(newPlan);
          } else {
            // Fallback: if plan not found in refetched data, use the uploaded plan data directly
            setSelectedPlan(uploadedPlan);
          }
        }
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  // Set up plan upload handler ref
  useEffect(() => {
    planUploadHandlerRef.current = () => {
      planFileInputRef.current?.click();
    };
  }, []);
  const teamMembers = (teamResponse as any)?.data || [];
  const teamGroups = (teamGroupsResponse as any)?.data || [];
  const statusList = statusResponse?.data || [];
  const gewerke = gewerkeResponse?.data || [];

  // Persist selected plan to localStorage (only when user explicitly changes selection, not during restore)
  useEffect(() => {
    if (!id || !hasRestoredPlanRef.current) {
      // Don't save during initial restore phase - wait until restore is complete
      previousSelectedPlanRef.current = selectedPlan;
      return;
    }

    // Only save if this is a real change (not initial mount)
    if (previousSelectedPlanRef.current === selectedPlan) {
      return;
    }

    const storageKey = `selectedPlan_${id}`;
    if (selectedPlan) {
      localStorage.setItem(storageKey, selectedPlan.id);
    } else {
      // User explicitly cleared selection
      localStorage.removeItem(storageKey);
    }

    previousSelectedPlanRef.current = selectedPlan;
  }, [selectedPlan, id]);

  // Reset restore flag when navigating away from planLevel tab
  useEffect(() => {
    if (activeTab !== 'planLevel') {
      // Reset flag when leaving planLevel tab so plan can be restored when coming back
      hasRestoredPlanRef.current = false;
    }
  }, [activeTab]);

  // Restore selected plan from localStorage when plans load or when switching to planLevel tab
  useEffect(() => {
    // Only restore when on planLevel tab
    if (activeTab !== 'planLevel') {
      return;
    }

    // Wait for plans to finish loading and ensure we have plans data
    if (!id || isLoadingPlans || !isPlansSuccess || !plansResponse || hasRestoredPlanRef.current) {
      return;
    }

    const plans = plansResponse?.data || [];
    if (!plans.length) {
      // No plans available, mark as restored
      hasRestoredPlanRef.current = true;
      return;
    }

    const storageKey = `selectedPlan_${id}`;
    const savedPlanId = localStorage.getItem(storageKey);

    if (savedPlanId) {
      const savedPlan = plans.find((p: any) => p.id === savedPlanId);
      if (savedPlan) {
        setSelectedPlan(savedPlan);
        previousSelectedPlanRef.current = savedPlan; // Track restored value so save effect doesn't trigger
        hasRestoredPlanRef.current = true;
      } else {
        // Plan no longer exists, remove from storage
        localStorage.removeItem(storageKey);
        previousSelectedPlanRef.current = null; // Track that we restored null
        hasRestoredPlanRef.current = true;
      }
    } else {
      // No saved plan, mark as restored to prevent further attempts
      previousSelectedPlanRef.current = null; // Track that we restored null
      hasRestoredPlanRef.current = true;
    }
  }, [plansResponse, isLoadingPlans, isPlansSuccess, id, activeTab]);

  // Reset restore flag when project changes
  useEffect(() => {
    hasRestoredPlanRef.current = false;
    previousSelectedPlanRef.current = null;
    previousPlanIdsRef.current = new Set();
    // Also clear selectedPlan when project changes
    setSelectedPlan(null);
  }, [id]);

  // Handle ticketId URL parameter - open ticket modal when navigating from notifications
  const hasOpenedTicketFromUrlRef = useRef<string | null>(null);
  useEffect(() => {
    // Only run if we have a ticketId param and tickets are loaded
    if (!ticketIdParam || ticketsLoading || tickets.length === 0) {
      return;
    }

    // Don't re-open if we already opened this ticket from URL
    if (hasOpenedTicketFromUrlRef.current === ticketIdParam) {
      return;
    }

    // Find the ticket from the loaded tickets
    const ticket = tickets.find((t: any) => t.id === ticketIdParam);
    if (ticket) {
      // Mark as opened to prevent re-triggering
      hasOpenedTicketFromUrlRef.current = ticketIdParam;

      // Select the ticket and open the inline edit modal
      setSelectedTicket(ticket);
      updateTicketForm(ticket);
      setShowInlineEditModal(true);

      // Clean up the URL by removing ticketId param after opening
      const [path] = location.split('?');
      const params = getSearchParamsFromLocation();
      params.delete('ticketId');
      const serializedSearch = params.toString();
      const nextLocation = serializedSearch ? `${path}?${serializedSearch}` : path;
      setLocation(nextLocation, { replace: true });
    }
  }, [ticketIdParam, tickets, ticketsLoading, location, getSearchParamsFromLocation, setLocation]);

  // Map semantic type to summary bucket
  const semanticTypeToBucket = (semanticType: string | null | undefined): 'deferred' | 'open' | 'inProgress' | 'completed' | null => {
    switch (semanticType) {
      case 'deferred': return 'deferred';
      case 'open': return 'open';
      case 'in_progress': return 'inProgress';
      case 'completed': return 'completed';
      default: return null;
    }
  };

  // Calculate ticket status counts and build allStatuses for custom statuses display
  const { ticketBuckets, ticketAllStatuses } = useMemo(() => {
    const buckets = { deferred: 0, open: 0, inProgress: 0, completed: 0 };
    const statusMap = new Map<string, { id: string; name: string; color: string; semanticType: SemanticType; count: number }>();

    for (const ticket of tickets) {
      const t = ticket as any;
      const semanticType = (t.statusSemanticType || 'other') as SemanticType;
      const bucket = semanticTypeToBucket(semanticType);
      if (bucket) buckets[bucket]++;

      // Track individual statuses for custom status display
      if (t.statusId) {
        const existing = statusMap.get(t.statusId);
        if (existing) {
          existing.count++;
        } else {
          statusMap.set(t.statusId, {
            id: t.statusId,
            name: t.status || 'Unknown',
            color: t.statusColor || '#6b7280',
            semanticType: semanticType,
            count: 1
          });
        }
      }
    }

    return {
      ticketBuckets: buckets,
      ticketAllStatuses: Array.from(statusMap.values())
    };
  }, [tickets]);

  // Update form when ticket is selected
  const updateTicketForm = (ticket: any) => {
    setTicketForm({
      title: ticket.title || '',
      description: ticket.description || '',
      status: ticket.status || '',
      priority: ticket.priority || '',
      trade: ticket.gewerk || '',
      type: ticket.type || '',
      location: ticket.location || '',
      cost: ticket.costs || '',
      assignedTo: ticket.assignedContractorId || '',
      dueDate: toDateInputValue(ticket.dueDate),
      extensionDate: toDateInputValue(ticket.extensionDate),
      progress: ticket.progress || 0,
      createdAt: ticket.createdAt || ticket.erstelltAm || ''
    });

    // Initialize images from ticket (prefer images array, fallback to individual fields)
    const images = (ticket as any).images || [];
    if (images.length > 0) {
      setTicketImages(images);
    } else {
      // Fallback to individual fields for backward compatibility
      const legacyImages: string[] = [];
      if ((ticket as any).image1Url) legacyImages.push((ticket as any).image1Url);
      if ((ticket as any).image2Url) legacyImages.push((ticket as any).image2Url);
      if ((ticket as any).image3Url) legacyImages.push((ticket as any).image3Url);
      if ((ticket as any).image4Url) legacyImages.push((ticket as any).image4Url);
      setTicketImages(legacyImages);
    }
  };

  // Handle form field changes
  const handleFormChange = (field: string, value: any) => {
    setTicketForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = async (file: File) => {
    if (!selectedTicket) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      await alert('Only image files are allowed');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      await alert('File is too large. Maximum: 10MB');
      return;
    }

    // Check max images limit
    if (ticketImages.length >= 10) {
      await alert('Maximum 10 images allowed per ticket');
      return;
    }

    try {
      // Compress image before upload
      const { compressImage } = await import('@/utils/image-compression');
      const compressedFile = await compressImage(file);

      // Fetch weather data for the photo (captured at photo addition time)
      let weatherDataForPhoto: any = null;
      if (selectedTicket.projectId) {
        try {
          const weatherResponse = await apiRequest('/api/weather', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ projectId: selectedTicket.projectId }),
          });

          if (weatherResponse.ok) {
            const weatherResult = await weatherResponse.json();
            if (weatherResult.success && weatherResult.data) {
              weatherDataForPhoto = {
                temperature: weatherResult.data.temperature,
                wind: weatherResult.data.wind,
                conditions: weatherResult.data.conditions,
                precipitation: weatherResult.data.precipitation,
                location: weatherResult.data.location,
                timestamp: weatherResult.data.timestamp || new Date(),
              };
            }
          }
        } catch (error) {
          console.error('Failed to fetch weather data for photo:', error);
          // Continue without weather data - photo upload should not fail
        }
      }

      // Upload to GCS via multipart, then attach URL to ticket
      const uploadResponse = await apiClient.uploadTicketImageToStore(compressedFile);
      const imageUrl = (uploadResponse as any)?.data?.imageUrl;
      if (!imageUrl) {
        await alert('Failed to upload image');
        return;
      }

      await apiClient.addTicketImage(selectedTicket.id, imageUrl, weatherDataForPhoto);

      setTicketImages([...ticketImages, imageUrl]);
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
      queryClient.invalidateQueries({ queryKey: [`/api/tickets/${selectedTicket.id}/image-metadata`] });

      if (imageUrl.startsWith('http') && hasPermission('ai_image_analysis')) {
        try {
          await apiClient.analyzeTicketImage(
            imageUrl,
            selectedTicket.id,
            {
              title: selectedTicket.title,
              description: selectedTicket.description,
              projectName: project?.name,
              projectId: id,
            }
          );
        } catch (analysisError) {
          console.error('AI analysis failed:', analysisError);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      await alert('Failed to upload image');
    }
  };

  // Handle image removal
  const handleImageRemove = async (index: number) => {
    if (!selectedTicket) return;
    try {
      const { apiClient } = await import('@/lib/api-client');
      await apiClient.deleteTicketImage(selectedTicket.id, index);
      setTicketImages(ticketImages.filter((_, i) => i !== index));
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
    } catch (error) {
      console.error('Delete error:', error);
      await alert('Failed to delete image');
    }
  };

  // Handle ticket save
  const handleTicketSave = () => {
    if (!selectedTicket) return;

    const ticketData = {
      title: ticketForm.title,
      description: ticketForm.description,
      status: ticketForm.status,
      priority: ticketForm.priority,
      gewerk: ticketForm.trade,
      type: ticketForm.type,
      location: ticketForm.location,
      costs: ticketForm.cost,
      assignedContractorId: ticketForm.assignedTo || null,
      dueDate: ticketForm.dueDate || null,
      extensionDate: ticketForm.extensionDate || null,
      progress: ticketForm.progress,
      images: ticketImages
    };

    console.log('🔧 Saving ticket data:', ticketData);
    updateTicketMutation.mutate(ticketData);
  };

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async (projectData: any) => {
      const response = await apiRequest(`/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        throw new Error('Failed to update project');
      }

      return response.json();
    },
    onMutate: async (projectData) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["/api/projects", id] }),
        queryClient.cancelQueries({ queryKey: ["/api/projects", id, "light"] }),
      ]);

      const previousProject = queryClient.getQueryData(["/api/projects", id]);
      const previousProjectLight = queryClient.getQueryData(["/api/projects", id, "light"]);
      const previousProjectLists = queryClient
        .getQueriesData({ queryKey: ["/api/projects"] })
        .filter(([, data]) => Array.isArray((data as any)?.data));
      const previousCurrency = currencyValue;

      updateProjectCache(projectData);
      updateProjectListCaches(projectData);

      if (projectData.currency) {
        setCurrencyValue(projectData.currency);
        justSavedCurrencyRef.current = projectData.currency;
      }

      return { previousProject, previousProjectLight, previousProjectLists, previousCurrency };
    },
    onSuccess: (result) => {
      // Show warning if coordinates don't match address
      if (result.warning) {
        setCoordinateWarning(result.warning);
      } else {
        setCoordinateWarning(undefined);
      }

      // Update the project query cache with the updated data from response
      if (result?.data) {
        const updatedProject = result.data;
        updateProjectCache(updatedProject);
        updateProjectListCaches(updatedProject);

        // Also ensure the currency is set immediately from the response
        if (updatedProject.currency) {
          setCurrencyValue(updatedProject.currency);
          justSavedCurrencyRef.current = updatedProject.currency;
        }
      }

      // Invalidate projects list query to refresh the projects page (but not the specific project query since we just updated it)
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      // Form stays in editing mode - no need to set isEditing to false
      setWeatherValidationError(null);
    },
    onError: (error: any, _projectData, context) => {
      if (context && 'previousProject' in context) {
        queryClient.setQueryData(["/api/projects", id], context.previousProject);
      }
      if (context && 'previousProjectLight' in context) {
        queryClient.setQueryData(["/api/projects", id, "light"], context.previousProjectLight);
      }
      if (context && 'previousProjectLists' in context && Array.isArray(context.previousProjectLists)) {
        context.previousProjectLists.forEach(([queryKey, data]: any) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context && 'previousCurrency' in context) {
        setCurrencyValue(context.previousCurrency);
        justSavedCurrencyRef.current = context.previousCurrency;
      }

      console.error('❌ Project update error:', error);

      // Check if it's a weather validation error
      const isWeatherValidationError = error?.message && (
        error.message.includes('Weather data cannot be retrieved') ||
        error.message.includes('Wetterdaten können nicht abgerufen werden') ||
        error.message.includes('Hava durumu verileri alınamıyor') ||
        error.code === 'WEATHER_VALIDATION_FAILED'
      );

      if (isWeatherValidationError) {
        setWeatherValidationError(error.message || t('form.validation.weatherValidationFailed', { ns: 'pages.projectDetails' }));
        toast({
          title: t('form.validation.weatherValidationFailed', { ns: 'pages.projectDetails' }),
          description: error.message || '',
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('pages.projectDetails:toasts.error'),
          description: error?.message || t('pages.projectDetails:toasts.updateError'),
          variant: 'destructive',
        });
      }
    },
  });

  const [showLogoCropModal, setShowLogoCropModal] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);

  // Handle project logo upload (after crop)
  const handleProjectLogoUpload = async (file: File) => {
    if (!id) return;

    const previousLogo = project?.projektLogo ?? null;
    const temporaryLogoUrl = URL.createObjectURL(file);

    if (optimisticLogoPreviewUrlRef.current) {
      URL.revokeObjectURL(optimisticLogoPreviewUrlRef.current);
    }
    optimisticLogoPreviewUrlRef.current = temporaryLogoUrl;
    updateProjectCache({ projektLogo: temporaryLogoUrl });

    try {
      // Upload to GCS
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('projectId', id);

      const uploadResponse = await apiRequest('/api/uploads/project-logo', {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload logo');
      }

      const uploadResult = await uploadResponse.json();

      if (uploadResult.success && uploadResult.data?.url) {
        // Update project with logo URL
        await updateProjectMutation.mutateAsync({ projektLogo: uploadResult.data.url });
      }
    } catch (error) {
      updateProjectCache({ projektLogo: previousLogo });
      console.error('Error uploading project logo:', error);
      await alert(t('pages.projectDetails:settings.logos.uploadFailed'));
    } finally {
      if (optimisticLogoPreviewUrlRef.current) {
        URL.revokeObjectURL(optimisticLogoPreviewUrlRef.current);
        optimisticLogoPreviewUrlRef.current = null;
      }
    }
  };

  // Handle logo file selection (before crop)
  const handleLogoFileSelect = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      await alert(t('pages.projectDetails:settings.logos.invalidFileType'));
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      await alert(t('pages.projectDetails:settings.logos.fileTooLarge'));
      return;
    }

    // Open crop modal
    setSelectedLogoFile(file);
    setShowLogoCropModal(true);
  };

  const handleLogoCropSave = (croppedFile: File) => {
    setShowLogoCropModal(false);
    setSelectedLogoFile(null);
    handleProjectLogoUpload(croppedFile);
  };

  const handleLogoCropCancel = () => {
    setShowLogoCropModal(false);
    setSelectedLogoFile(null);
  };

  // Handle project logo removal
  const handleRemoveProjectLogo = async () => {
    if (!id) return;

    const result = await confirm({
      message: t('pages.projectDetails:settings.logos.confirmRemove'),
      variant: 'destructive'
    });
    if (!result) {
      return;
    }

    updateProjectMutation.mutate({ projektLogo: null });
  };

  // Update ticket mutation
  const updateTicketMutation = useMutation({
    mutationFn: async (ticketData: any) => {
      const response = await apiRequest(`/api/tickets/${selectedTicket.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketData),
      });

      if (!response.ok) {
        throw new Error('Failed to update ticket');
      }

      return response.json();
    },
    onMutate: async (ticketData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.projectTickets(id) });

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData(QUERY_KEYS.projectTickets(id));

      // Optimistically update the cache
      queryClient.setQueryData(QUERY_KEYS.projectTickets(id), (oldData: any) => {
        if (!oldData?.data) return oldData;

        return {
          ...oldData,
          data: oldData.data.map((ticket: any) =>
            ticket.id === selectedTicket.id
              ? { ...ticket, ...ticketData }  // Optimistically update with form data
              : ticket
          )
        };
      });

      // Return a context object with the snapshotted value
      return { previousTickets };
    },
    onSuccess: (data) => {
      console.log('✅ Ticket update successful:', data);

      // Update the ticket in the project tickets cache immediately
      queryClient.setQueryData(QUERY_KEYS.projectTickets(id), (oldData: any) => {
        if (!oldData?.data) return oldData;

        return {
          ...oldData,
          data: oldData.data.map((ticket: any) =>
            ticket.id === selectedTicket.id
              ? { ...ticket, ...data.data }  // Update the specific ticket with new data
              : ticket
          )
        };
      });

      // Update the selected ticket with the new data
      if (data?.data) {
        console.log('🔄 Updating selected ticket with:', data.data);
        setSelectedTicket(data.data);
        updateTicketForm(data.data);
      }

      // Close the modal automatically after successful save
      setShowTicketDetail(false);
      setSelectedTicket(null);
    },
    onError: (error, ticketData, context) => {
      console.error('Failed to update ticket:', error);

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTickets) {
        queryClient.setQueryData(QUERY_KEYS.projectTickets(id), context.previousTickets);
      }

      // You could add a toast notification here
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTickets(id) });
      // Also invalidate history cache for the updated ticket
      if (selectedTicket?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/tickets/${selectedTicket.id}/history`] });
      }
    },
  });

  // Delete ticket mutation
  const deleteTicketMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      const response = await apiRequest(`/api/tickets/${ticketId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete ticket');
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          return await response.json();
        } catch (error) {
          // If JSON parsing fails, return success anyway since DELETE might return empty body
          return { success: true };
        }
      } else {
        // No JSON content, assume success for DELETE operations
        return { success: true };
      }
    },
    onSuccess: (data, ticketId) => {
      console.log('✅ Ticket deleted successfully:', ticketId);

      // Remove the ticket from the cache immediately using the correct query key
      queryClient.setQueryData(QUERY_KEYS.projectTickets(id), (oldData: any) => {
        if (!oldData?.data) return oldData;

        return {
          ...oldData,
          data: oldData.data.filter((ticket: any) => ticket.id !== ticketId)
        };
      });

      // Close the confirmation dialog
      handleDeleteCancel();
    },
    onError: (error) => {
      console.error('❌ Failed to delete ticket:', error);
      // Close the confirmation dialog even on error
      handleDeleteCancel();
    },
  });

  // Handle delete confirmation
  const handleDeleteConfirm = (ticket: any) => {
    setTicketToDelete(ticket);
    setShowDeleteConfirm(true);
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setTicketToDelete(null);
  };

  // Check if form has been modified
  const isFormDirty = useMemo(() => {
    if (!initialProjectValues) return false;

    return (
      projektNummer !== initialProjectValues.projektNummer ||
      projektName !== initialProjectValues.projektName ||
      street !== initialProjectValues.street ||
      houseNo !== initialProjectValues.houseNo ||
      addressSupplement !== initialProjectValues.addressSupplement ||
      city !== initialProjectValues.city ||
      postalCode !== initialProjectValues.postalCode ||
      country !== initialProjectValues.country ||
      latitude !== initialProjectValues.latitude ||
      longitude !== initialProjectValues.longitude ||
      auftraggeber !== initialProjectValues.auftraggeber ||
      clientTypeId !== initialProjectValues.clientTypeId ||
      currencyValue !== initialProjectValues.currency ||
      projektleiter !== initialProjectValues.projektleiter ||
      projektleiterId !== initialProjectValues.projektleiterId ||
      managerTeamEntryId !== initialProjectValues.managerTeamEntryId ||
      projectStatusId !== initialProjectValues.projectStatusId ||
      startDatum !== initialProjectValues.startDatum ||
      endDatum !== initialProjectValues.endDatum ||
      fortschritt !== initialProjectValues.fortschritt
    );
  }, [
    initialProjectValues,
    projektNummer,
    projektName,
    street,
    houseNo,
    addressSupplement,
    city,
    postalCode,
    country,
    latitude,
    longitude,
    auftraggeber,
    clientTypeId,
    currencyValue,
    projektleiter,
    projektleiterId,
    managerTeamEntryId,
    projectStatusId,
    startDatum,
    endDatum,
    fortschritt,
  ]);

  // Validate weather data can be retrieved
  const validateWeatherData = async (projectData: any): Promise<boolean> => {
    setIsValidatingWeather(true);
    setWeatherValidationError(null);

    try {
      const response = await apiRequest('/api/weather', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          projectData.latitude && projectData.longitude
            ? {
              latitude: projectData.latitude,
              longitude: projectData.longitude,
            }
            : {
              street: projectData.street || undefined,
              houseNo: projectData.houseNo || undefined,
              city: projectData.city || undefined,
              postalCode: projectData.postalCode || undefined,
              country: projectData.country || undefined,
            }
        ),
      });

      const data = await response.json();
      if (!data.success) {
        setWeatherValidationError(data.message || t('form.validation.weatherValidationFailed', { ns: 'pages.projectDetails' }));
        return false;
      }
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        t('form.validation.weatherValidationFailed', { ns: 'pages.projectDetails' });
      setWeatherValidationError(errorMessage);
      return false;
    } finally {
      setIsValidatingWeather(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!canEditProjectDetails) {
      await alert({
        message: t('common:accessDenied.message', "You don't have permission to modify project details"),
        title: t('common:accessDenied.title', 'Access Denied'),
      });
      return;
    }

    // Validate required fields
    const trimmedCity = city?.trim() || '';
    const trimmedCountry = country?.trim() || '';

    if (!trimmedCity) {
      toast({
        title: t('form.validation.cityRequired', { ns: 'pages.projectDetails' }),
        variant: 'destructive',
      });
      return;
    }

    if (!trimmedCountry) {
      toast({
        title: t('form.validation.countryRequired', { ns: 'pages.projectDetails' }),
        variant: 'destructive',
      });
      return;
    }

    const projectData: any = {
      projektNummer,
      name: projektName,
      street: street || undefined,
      houseNo: houseNo || undefined,
      addressSupplement: addressSupplement || undefined,
      city: trimmedCity,
      postalCode: postalCode || undefined,
      country: trimmedCountry,
      latitude: latitude || undefined,
      longitude: longitude || undefined,
      auftraggeber,
      clientTypeId: clientTypeId ?? null,
      currency: currencyValue,
      managerTeamEntryId,
      projectStatusId: projectStatusId || undefined,
      startDatum: startDatum || null,
      endDatum: endDatum || null,
      fortschritt
    };

    // Validate weather data before submitting
    const isValid = await validateWeatherData(projectData);
    if (!isValid) {
      // Error is already set in validateWeatherData
      toast({
        title: t('form.validation.weatherValidationFailed', { ns: 'pages.projectDetails' }),
        description: weatherValidationError || '',
        variant: 'destructive',
      });
      return;
    }

    updateProjectMutation.mutate(projectData, {
      onSuccess: (result) => {
        // Update initial values after successful save with the saved values
        if (initialProjectValues) {
          const updatedProject = result?.data || projectData;
          const savedCurrency = updatedProject.currency || currencyValue;

          // Update initial values immediately to prevent useEffect from overwriting
          setInitialProjectValues({
            projektNummer,
            projektName,
            street,
            houseNo,
            addressSupplement,
            city,
            postalCode,
            country,
            latitude,
            longitude,
            auftraggeber,
            clientTypeId,
            currency: savedCurrency,
            projektleiter,
            projektleiterId,
            managerTeamEntryId,
            projectStatusId,
            startDatum,
            endDatum,
            fortschritt,
          });

          // Update the currency value state to match what was saved
          setCurrencyValue(savedCurrency);
          // Track that we just saved this currency to prevent useEffect from overwriting it
          justSavedCurrencyRef.current = savedCurrency;
          // Clear the ref after a longer delay to allow refetch to complete and ensure cache is updated
          setTimeout(() => {
            justSavedCurrencyRef.current = null;
          }, 5000); // Increased to 5 seconds to ensure cache update completes
        }
      },
    });
  };

  // Handle cancel - reset to original values but keep editing mode
  const handleCancel = () => {
    if (initialProjectValues) {
      setProjektNummer(initialProjectValues.projektNummer);
      setProjektName(initialProjectValues.projektName);
      setStreet(initialProjectValues.street);
      setHouseNo(initialProjectValues.houseNo);
      setCity(initialProjectValues.city);
      setPostalCode(initialProjectValues.postalCode);
      setCountry(initialProjectValues.country);
      setLatitude(initialProjectValues.latitude);
      setLongitude(initialProjectValues.longitude);
      setAuftraggeber(initialProjectValues.auftraggeber);
      setClientTypeId(initialProjectValues.clientTypeId);
      setCurrencyValue(initialProjectValues.currency);
      setProjektleiter(initialProjectValues.projektleiter);
      setProjektleiterId(initialProjectValues.projektleiterId);
      setManagerTeamEntryId(initialProjectValues.managerTeamEntryId);
      setProjectStatusId(initialProjectValues.projectStatusId);
      setCoordinateWarning(undefined);
      setStartDatum(initialProjectValues.startDatum);
      setEndDatum(initialProjectValues.endDatum);
      setFortschritt(initialProjectValues.fortschritt);
    }
    // Don't exit editing mode - form stays editable
  };

  // Handle status change
  const handleStatusChange = (newProjectStatusId: string) => {
    setProjectStatusId(newProjectStatusId);
  };

  // Handle start date change
  const handleStartDateChange = (newDate: string) => {
    setStartDatum(newDate);
    setIsEditingStartDate(false);
  };

  // Handle end date change
  const handleEndDateChange = (newDate: string) => {
    setEndDatum(newDate);
    setIsEditingEndDate(false);
  };

  // Handle progress change
  const handleProgressChange = (newProgress: number) => {
    setFortschritt(newProgress);
    const syncedStatusId = resolveProjectStatusIdForProgress(
      newProgress,
      projectStatusId,
      projectStatuses,
    );
    if (syncedStatusId !== projectStatusId) {
      setProjectStatusId(syncedStatusId ?? null);
    }
    debouncedSave(newProgress);
  };

  // Handle plan delete
  const handlePlanDelete = async (planId: string) => {
    const result = await confirm({
      title: t('pages.projectDetails:confirmations.deletePlan', 'Delete Plan'),
      message: t('pages.projectDetails:confirmations.deletePlanMessage', 'Are you sure you want to delete this plan?'),
      variant: 'destructive'
    });
    if (!result) return;

    setIsDeletingPlan(true);
    try {
      const response = await apiRequest(`/api/plans/${planId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectPlans(id) });
        if (selectedPlan?.id === planId) {
          setSelectedPlan(null);
        }
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
    } finally {
      setIsDeletingPlan(false);
    }
  };

  // Handle plan upload
  const handlePlanUpload = async () => {
    if (!planName.trim() || !planFile) return;

    setIsUploading(true);
    try {
      // Create FormData for multipart upload to GCS
      const formData = new FormData();
      formData.append('file', planFile);
      formData.append('projectId', id!);
      formData.append('name', planName);
      formData.append('description', '');

      const response = await apiRequest('/api/plans', {
        method: 'POST',
        // Don't set Content-Type - browser will set it with boundary
        body: formData,
      });

      if (response.ok) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectPlans(id) });
        setShowPlanUpload(false);
        setPlanName('');
        setPlanFile(null);
      }
    } catch (error) {
      console.error('Error uploading plan:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Fetch priorities for display
  const { data: priorities = [] } = usePriorities();

  // Legacy helpers for backward compatibility
  const getPriorityCode = (raw?: string | null): 'low' | 'normal' | 'high' | 'critical' | null => {
    if (!raw) return null;
    const v = String(raw).toLowerCase();
    if (['niedrig', 'low', 'düşük'].includes(v)) return 'low';
    if (['mittel', 'normal', 'medium', 'orta'].includes(v)) return 'normal';
    if (['hoch', 'high', 'yüksek'].includes(v)) return 'high';
    if (['kritisch', 'critical', 'kritik'].includes(v)) return 'critical';
    return null;
  };

  const getPriorityColorClasses = (code: 'low' | 'normal' | 'high' | 'critical' | null) => {
    switch (code) {
      case 'low': return 'bg-blue-100 text-blue-800';
      case 'normal': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getPriorityLabel = (raw?: string | null) => {
    const code = getPriorityCode(raw);
    if (code === 'low') return t('components.ticket:priority.low');
    if (code === 'normal') return t('components.ticket:priority.medium');
    if (code === 'high') return t('components.ticket:priority.high');
    if (code === 'critical') return t('components.ticket:priority.critical');
    return raw || '-';
  };

  // Get project weather helper
  const getProjectWeatherData = (ticketId: string) => {
    return getProjectWeather(ticketId);
  };

  // Handle ticket click from PDF viewer pins
  const handleTicketClick = (ticket: any) => {
    setSelectedTicket(ticket);
    updateTicketForm(ticket);
    setShowInlineEditModal(true);
  };

  // Prepare plan preview for Ticket Detail modal (lazy-load pdfjs service)
  useEffect(() => {
    let cancelled = false;
    async function prepareDetailPlan() {
      if (!showTicketDetail || !selectedTicket?.planLocation) {
        setDetailPlanImage(null);
        setDetailPlanPin(null);
        return;
      }
      try {
        const plan = plans.find(p => p.id === selectedTicket.planLocation.planId);
        if (plan?.fileUrl) {
          const svc = await ensurePlanImageCaptureService();
          const { imageData, pinPosition } = await svc.capturePlanSection(
            plan.fileUrl,
            selectedTicket.planLocation,
            1,
            { width: 600, height: 400 }
          );
          if (!cancelled) {
            setDetailPlanImage(imageData);
            setDetailPlanPin(pinPosition);
          }
        } else {
          setDetailPlanImage(null);
          setDetailPlanPin(null);
        }
      } catch (e) {
        console.error('Failed to prepare plan preview for ticket detail:', e);
        setDetailPlanImage(null);
        setDetailPlanPin(null);
      }
    }
    prepareDetailPlan();
    return () => { cancelled = true; };
  }, [showTicketDetail, selectedTicket?.id]);

  // Transform ticket data for PDF export (used by edit modal)
  const transformTicketForExport = async (ticket: any, options: { includePhotos: boolean; includeHistory: boolean; includeMessages: boolean }) => {
    const plan = plans.find(p => p.id === ticket.planLocation?.planId);

    let planImageData: string | undefined;
    let planImagePinPosition: { x: number; y: number } | undefined;

    // Use stored preview image if available (preferred - same settings as PDF export)
    if (ticket.planPreviewImage) {
      planImageData = ticket.planPreviewImage;
      planImagePinPosition = ticket.planPreviewPinPosition;
    }
    // Otherwise, generate on-the-fly (backward compatibility for old tickets)
    else if (ticket.planLocation && plan?.fileUrl) {
      try {
        const svc = await ensurePlanImageCaptureService();
        const captureResult = await svc.capturePlanSection(
          plan.fileUrl,
          ticket.planLocation,
          2, // zoom level (2x for better detail)
          { width: 400, height: 300 }, // capture size
          ticket.ticketNumber // render pin with ticket number in image
        );
        planImageData = captureResult.imageData;
        planImagePinPosition = captureResult.pinPosition; // Keep for backward compatibility but pin is in image
      } catch {
        // Continue without image data
      }
    }

    // Helper function to convert image URL to base64
    const convertImageUrlToBase64 = async (imageUrl: string): Promise<string | null> => {
      try {
        // If already base64, return as is
        if (imageUrl.startsWith('data:image/')) {
          return imageUrl;
        }

        // If blob URL, fetch and convert
        if (imageUrl.startsWith('blob:')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }

        // For regular URLs (GCS, etc.), fetch and convert
        const response = await fetch(imageUrl);
        if (!response.ok) {
          return null; // Image not accessible (might be deleted)
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return null; // Return null for failed conversions (deleted/invalid images)
      }
    };

    // Fetch additional data based on options
    let photos: string[] | undefined;
    let history: any[] | undefined;
    let messages: any[] | undefined;

    if (options.includePhotos) {
      // Photos are already in ticket.images array
      // Convert URLs to base64 and filter out invalid/deleted images
      const imageUrls = (ticket.images || []) as string[];
      const convertedImages = await Promise.all(
        imageUrls.map(url => convertImageUrlToBase64(url))
      );
      // Filter out null values (failed conversions = deleted/invalid images)
      photos = convertedImages.filter((img): img is string => img !== null);
    }

    if (options.includeHistory) {
      try {
        const historyResponse = await apiClient.getTicketHistory(ticket.id, {
          limit: 200,
          offset: 0,
        });
        history = historyResponse.items;
      } catch {
        history = [];
      }
    }

    if (options.includeMessages) {
      try {
        const chatData = await apiClient.getTicketChat(ticket.id);
        messages = chatData.messages || [];
      } catch {
        messages = [];
      }
    }

    return {
      id: ticket.id,
      ticketNumber: ticket.ticketNumber,
      title: ticket.title,
      status: ticket.status,
      prioritaet: ticket.priority,
      zugewiesenePerson: ticket.assigneeName || ticket.assignedContractorId,
      assignedContractorId: ticket.assignedContractorId,
      standort: ticket.location,
      beschreibung: ticket.description,
      gewerk: ticket.gewerk,
      typ: ticket.type,
      faelligkeitsdatum: ticket.dueDate,
      geschaetzteKosten: ticket.costs,
      fortschritt: ticket.progress,
      planLocation: ticket.planLocation,
      planName: plan?.name,
      planId: plan?.id,
      weatherData: 'Keine Wetterdaten verfügbar', // Default value
      planImageData: planImageData,
      planImagePinPosition: planImagePinPosition,
      photos,
      history,
      messages
    };
  };

  // Transform ticket data for Tickets Tab PDF export (mobile-matching layout)
  // This returns the extended data shape required by TicketPDFExportServiceTicketsTab
  const transformTicketForExportTicketsTab = async (
    ticket: any,
    options: { includePhotos: boolean; includeHistory: boolean; includeMessages: boolean }
  ): Promise<TicketExportDataTicketsTab> => {
    const plan = plans.find(p => p.id === ticket.planLocation?.planId);

    // Helper function to convert image URL to base64 - defined first so it can be used below
    const convertImageUrlToBase64 = async (imageUrl: string): Promise<string | null> => {
      try {
        if (imageUrl.startsWith('data:image/')) {
          return imageUrl;
        }
        if (imageUrl.startsWith('blob:')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }
        // For GCS URLs, use crossorigin fetch
        const response = await fetch(imageUrl, { mode: 'cors' });
        if (!response.ok) {
          console.warn('[PDF Export] Failed to fetch image:', imageUrl, response.status);
          return null;
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('[PDF Export] Error converting image to base64:', imageUrl, error);
        return null;
      }
    };

    let planImageData: string | undefined;

    // Use stored preview image if available
    if (ticket.planPreviewImage) {
      console.log('[PDF Export] Found plan preview for ticket', ticket.ticketNumber, '- converting to base64');
      // planPreviewImage might be a URL or base64, need to convert if URL
      if (ticket.planPreviewImage.startsWith('data:image')) {
        planImageData = ticket.planPreviewImage;
      } else {
        // It's a URL, convert to base64
        planImageData = await convertImageUrlToBase64(ticket.planPreviewImage) || undefined;
      }
      if (planImageData) {
        console.log('[PDF Export] Successfully converted plan preview to base64');
      } else {
        console.warn('[PDF Export] Failed to convert plan preview to base64');
      }
    }
    // Otherwise, generate on-the-fly
    else if (ticket.planLocation && plan?.fileUrl) {
      try {
        console.log('[PDF Export] Generating plan preview for ticket', ticket.ticketNumber);
        const svc = await ensurePlanImageCaptureService();
        const captureResult = await svc.capturePlanSection(
          plan.fileUrl,
          ticket.planLocation,
          2,
          { width: 400, height: 300 },
          ticket.ticketNumber
        );
        planImageData = captureResult.imageData;
        console.log('[PDF Export] Successfully generated plan preview');
      } catch (error) {
        console.error('[PDF Export] Failed to generate plan preview:', error);
        // Continue without image data
      }
    } else {
      console.log('[PDF Export] No plan preview available for ticket', ticket.ticketNumber, '- no planPreviewImage and no planLocation/plan');
    }

    // Fetch additional data based on options
    let photos: string[] | undefined;
    let history: any[] | undefined;
    let messages: any[] | undefined;

    if (options.includePhotos) {
      const imageUrls = (ticket.images || []) as string[];
      console.log('[PDF Export] Converting', imageUrls.length, 'images for ticket', ticket.ticketNumber);
      const convertedImages = await Promise.all(
        imageUrls.map(url => convertImageUrlToBase64(url))
      );
      photos = convertedImages.filter((img): img is string => img !== null);
      console.log('[PDF Export] Successfully converted', photos.length, 'of', imageUrls.length, 'images');
    }

    if (options.includeHistory) {
      try {
        const historyResponse = await apiClient.getTicketHistory(ticket.id, {
          limit: 200,
          offset: 0,
        });
        history = historyResponse.items;
      } catch {
        history = [];
      }
    }

    if (options.includeMessages) {
      try {
        const chatData = await apiClient.getTicketChat(ticket.id);
        messages = chatData.messages || [];
      } catch {
        messages = [];
      }
    }

    // Build weather text from ticket weather data
    const buildWeatherText = () => {
      const parts: string[] = [];
      if (ticket.weatherData?.temperature) parts.push(ticket.weatherData.temperature);
      if (ticket.weatherData?.conditions) parts.push(ticket.weatherData.conditions);
      if (ticket.weatherData?.wind) parts.push(`Wind ${ticket.weatherData.wind}`);
      return parts.length > 0 ? parts.join(', ') : undefined;
    };

    // Log what we're returning
    console.log('[PDF Export] Transform result for ticket', ticket.ticketNumber, {
      hasPlanImageData: !!planImageData,
      planImageDataPrefix: planImageData?.substring(0, 50),
      photosCount: photos?.length,
      firstPhotoPrefix: photos?.[0]?.substring(0, 50),
    });

    // Return extended data shape for tickets-tab PDF
    return {
      id: ticket.id,
      ticketNumber: ticket.ticketNumber,
      title: ticket.title,
      status: ticket.status,
      priority: ticket.priority,
      assignedContractorName: ticket.assignedContractorName,
      assigneeName: ticket.assigneeName,
      location: ticket.location,
      description: ticket.description,
      gewerk: ticket.gewerk,
      typ: ticket.type,
      dueDate: ticket.dueDate,
      extensionDate: ticket.extensionDate,
      costs: ticket.costs,
      progress: ticket.progress,
      planLocation: ticket.planLocation,
      planName: plan?.name,
      planId: plan?.id,
      planImageData: planImageData,
      photos: photos,
      history,
      messages,
      // Extended fields for mobile-matching layout
      creatorName: ticket.creatorName || ticket.reporterName,
      reporterName: ticket.reporterName,
      creatorProfilePhotoUrl: ticket.creatorProfilePhotoUrl
        ? await convertImageUrlToBase64(ticket.creatorProfilePhotoUrl) || undefined
        : undefined,
      projectLogoUrl: project?.projektLogo
        ? await convertImageUrlToBase64(project.projektLogo) || undefined
        : undefined,
      projectName: project?.name || 'Project',
      projectCity: project?.city || project?.stadt,
      currency: currency || 'EUR',
      updatedAt: ticket.updatedAt,
      overtimeNeeded: ticket.overtimeNeeded,
      overtimeHours: ticket.overtimeHours,
      overtimeConfirmed: ticket.overtimeConfirmed,
      weatherTemperature: ticket.weatherData?.temperature,
      weatherConditions: ticket.weatherData?.conditions,
      weatherWind: ticket.weatherData?.wind,
    };
  };

  // Calculate total likes - must be before any early returns to follow Rules of Hooks
  const totalLikes = useMemo(() => {
    return tickets.reduce((sum: number, ticket: any) => {
      const likes = Number(ticket?.likes);
      return sum + (Number.isFinite(likes) && likes > 0 ? likes : 0);
    }, 0);
  }, [tickets]);

  if (error) {
    const code = (error as Error & { code?: string }).code;
    if (code === 'PROJECTS_SUBSCRIPTION_HIDDEN') {
      return (
        <AppShell>
          <div className="flex-1 flex items-center justify-center">
            <LogoLoader size="md" text={t('pages.projects:subscriptionHidden.redirecting')} showText />
          </div>
        </AppShell>
      );
    }
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Project</h2>
            <p className="text-slate-600 mb-4">Failed to load project data</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!project) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <LogoLoader size="md" />
        </div>
      </AppShell>
    );
  }

  // Render search component based on active tab
  const renderSearchComponent = () => {
    if (activeTab === 'tickets') {
      return (
        <ProjectHeaderSearch
          searchTerm={globalTicketSearchTerm}
          setSearchTerm={setGlobalTicketSearchTerm}
        />
      );
    }
    if (activeTab === 'team') {
      return (
        <ProjectHeaderTeamSearch
          searchTerm={globalTeamSearchTerm}
          setSearchTerm={setGlobalTeamSearchTerm}
        />
      );
    }
    if (activeTab === 'groups') {
      return (
        <HeaderSearchInput
          value={globalGroupSearchTerm}
          onChange={setGlobalGroupSearchTerm}
          placeholder={t('team.searchGroups')}
        />
      );
    }
    if (activeTab === 'planLevel') {
      return (
        <HeaderSearchInput
          value={globalTicketSearchTerm}
          onChange={setGlobalTicketSearchTerm}
          placeholder={t('tickets.searchPlaceholder')}
        />
      );
    }
    if (activeTab === 'details') {
      return (
        <HeaderSearchInput
          value={detailsSearchTerm}
          onChange={setDetailsSearchTerm}
          placeholder={t('pages.projects:searchPlaceholder')}
        />
      );
    }
    if (activeTab === 'documents') {
      return (
        <HeaderSearchInput
          value={globalDocumentsSearchTerm}
          onChange={setGlobalDocumentsSearchTerm}
          placeholder={t('documents.searchPlaceholder')}
        />
      );
    }
    return null;
  };

  // Render tabs
  // All tabs are always visible - unauthorized users will see AccessDenied when clicking
  const renderTabs = () => {
    return (
      <HeaderTabNav
        items={[
          {
            id: 'details',
            label: t('tabs.details'),
            active: activeTab === 'details',
            onClick: () => handleTabChange('details'),
          },
          {
            id: 'planLevel',
            label: t('tabs.planLevel'),
            active: activeTab === 'planLevel',
            onClick: () =>
              handleTabChange('planLevel', {
                keepPlanLevelControls: true,
                keepStartPinPlacement: true,
              }),
          },
          {
            id: 'tickets',
            label: t('tabs.tickets'),
            active: activeTab === 'tickets',
            onClick: () => handleTabChange('tickets'),
          },
          {
            id: 'team',
            label: t('tabs.team'),
            active: activeTab === 'team',
            onClick: () => handleTabChange('team'),
          },
          {
            id: 'groups',
            label: t('tabs.groups'),
            active: activeTab === 'groups',
            onClick: () => handleTabChange('groups'),
          },
          {
            id: 'documents',
            label: t('tabs.documents'),
            active: activeTab === 'documents',
            onClick: () => handleTabChange('documents'),
          },
          ...(canManageProjectSettings()
            ? [
              {
                id: 'einstellungen',
                label: t('tabs.settings'),
                active: activeTab === 'einstellungen',
                onClick: () => handleTabChange('einstellungen'),
              },
            ]
            : []),
        ]}
      />
    );
  };

  // Render action buttons
  const renderActionButtons = () => {
    if (activeTab === 'details') {
      return null;
    }
    if (activeTab === 'planLevel') {
      return (
        <div className="flex items-center space-x-2">
          {canManageDocuments && (
            <Button
              variant="pillOutline"
              size="sm"
              disabled={isUploadingPlan}
              onClick={() => {
                if (planUploadHandlerRef.current) {
                  planUploadHandlerRef.current();
                }
              }}
              className="bg-white hover:bg-white"
            >
              {isUploadingPlan ? <LogoLoader size="sm" /> : <Upload />}
              {isUploadingPlan ? t('ui.uploading') : t('ui.uploadPlan')}
            </Button>
          )}
          {canCreateTickets() && canManagePlans && (
            <Button
              variant="pillOutline"
              size="sm"
              onClick={async () => {
                if (isContractor) {
                  await alert({
                    message: t('common:accessDenied.message', 'You don\'t have permission to access this section'),
                    title: t('common:accessDenied.title', 'Access Denied'),
                  });
                  return;
                }
                if (startPinPlacementRef.current) {
                  try {
                    startPinPlacementRef.current();
                  } catch (error) {
                    console.error('Error starting pin placement:', error);
                  }
                }
              }}
              disabled={!selectedPlan}
              className="bg-white hover:bg-white"
            >
              <Plus />
              {t('tickets.newTicket')}
            </Button>
          )}
        </div>
      );
    }
    if (activeTab === 'tickets') {
      return (
        <div className="flex items-center space-x-2">
          <Button
            variant="pillOutline"
            size="sm"
            onClick={async () => {
              // Check if user has permission to create tickets
              if (!canCreateTickets()) {
                await alert({
                  message: t('common:accessDenied.message', 'You don\'t have permission to create tickets'),
                  title: t('common:accessDenied.title', 'Access Denied'),
                });
                return;
              }
              setShowNewTicketModal(true);
            }}
            className="bg-white hover:bg-white"
          >
            <Plus />
            {t('tickets.newTicket')}
          </Button>
        </div>
      );
    }
    if (activeTab === 'team') {
      if (!canManageTeamMembers) return null;
      return (
        <div className="flex items-center space-x-2">
          <Button
            variant="pillOutline"
            size="sm"
            onClick={() => openAddMemberRef.current?.()}
            className="bg-white hover:bg-white"
          >
            <Plus className="h-4 w-4" />
            {t('team.addMember')}
          </Button>
        </div>
      );
    }
    if (activeTab === 'groups') {
      if (!canManageGroups) return null;
      return (
        <div className="flex items-center space-x-2">
          <Button
            variant="pillOutline"
            size="sm"
            onClick={() => openCreateGroupRef.current?.()}
            className="bg-white hover:bg-white"
          >
            <Plus className="h-4 w-4" />
            {t('team.createGroup')}
          </Button>
        </div>
      );
    }
    if (activeTab === 'documents') {
      // Only show upload button if user can manage the project scope
      if (!canManageDocuments) {
        return null;
      }
      return (
        <Button
          variant="pillOutline"
          size="sm"
          onClick={() => {
            // Trigger upload from ProjectDocumentsTab
            if (documentsUploadHandlerRef.current) {
              documentsUploadHandlerRef.current();
            }
          }}
          className="bg-white hover:bg-white"
        >
          <Upload />
          {t('documents.upload')}
        </Button>
      );
    }
    return null;
  };

  return (
    <AppShell>
      <PageHeaderLayout
        pageTitle={project?.name || 'Loading...'}
        pageSubtitle={project?.projektNummer}
        searchComponent={renderSearchComponent()}
        ticketSummaryCards={activeTab === 'details' || activeTab === 'planLevel' || activeTab === 'tickets' || activeTab === 'team' || activeTab === 'groups' || activeTab === 'documents' || activeTab === 'einstellungen' ? <TicketSummaryCards buckets={ticketBuckets} allStatuses={ticketAllStatuses} /> : undefined}
        likeCount={activeTab === 'details' || activeTab === 'planLevel' || activeTab === 'tickets' || activeTab === 'team' || activeTab === 'groups' || activeTab === 'documents' || activeTab === 'einstellungen' ? totalLikes : undefined}
        tabs={renderTabs()}
        middleControls={activeTab === 'planLevel' ? planLevelMiddleControls : activeTab === 'tickets' ? (
          <div className="flex items-center gap-2">
            <select
              value={ticketsTabPlanLevelId}
              onChange={(e) => setTicketsTabPlanLevelId(e.target.value)}
              className="textbox-base textbox-pill text-sm min-w-[200px] bg-white"
              style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
            >
              <option value="">{t('tickets.planLevelAll')}</option>
              {(plans || []).map((plan: any) => {
                const planTickets = (tickets || []).filter((ticket: any) => ticket.planLocation?.planId === plan.id);
                return (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ({planTickets.length} {planTickets.length === 1 ? 'ticket' : 'tickets'})
                  </option>
                );
              })}
            </select>
          </div>
        ) : undefined}
        actionButtons={renderActionButtons()}
      >
        {/* Tab Content */}
        <div className={`h-full flex flex-col ${activeTab === 'details' || activeTab === 'einstellungen' ? 'overflow-y-auto' : 'overflow-hidden'}`}>
          {activeTab === 'details' && (
            <ProjectDetailsTab
              weatherValidationError={weatherValidationError}
              isValidatingWeather={isValidatingWeather}
              project={project}
              tickets={tickets}
              documents={documents}
              teamMembers={teamMembers}
              ticketStatusCounts={{ postponed: ticketBuckets.deferred, open: ticketBuckets.open, inProgress: ticketBuckets.inProgress, completed: ticketBuckets.completed }}
              isEditing={isEditing && canEditProjectDetails}
              onEdit={() => {
                if (canEditProjectDetails) {
                  setIsEditing(true);
                }
              }}
              onSave={handleSave}
              onCancel={handleCancel}
              isSaving={updateProjectMutation.isPending}
              isFormDirty={isFormDirty}
              isContractor={isContractor || !canEditProjectDetails}
              onImageUpdate={canEditProjectDetails ? (imageUrl) => {
                if (project) {
                  updateProjectMutation.mutate({ bildUrl: imageUrl });
                }
              } : undefined}
              onLogoUpload={canEditProjectDetails ? handleLogoFileSelect : undefined}
              onLogoRemove={canEditProjectDetails ? handleRemoveProjectLogo : undefined}
              projektNummer={projektNummer}
              setProjektNummer={setProjektNummer}
              projektName={projektName}
              setProjektName={setProjektName}
              street={street}
              setStreet={setStreet}
              houseNo={houseNo}
              setHouseNo={setHouseNo}
              addressSupplement={addressSupplement}
              setAddressSupplement={setAddressSupplement}
              city={city}
              setCity={setCity}
              postalCode={postalCode}
              setPostalCode={setPostalCode}
              country={country}
              setCountry={setCountry}
              latitude={latitude}
              setLatitude={setLatitude}
              longitude={longitude}
              setLongitude={setLongitude}
              coordinateWarning={coordinateWarning}
              auftraggeber={auftraggeber}
              setAuftraggeber={setAuftraggeber}
              clientTypeId={clientTypeId}
              setClientTypeId={setClientTypeId}
              currency={currencyValue}
              setCurrency={setCurrencyValue}
              projektleiter={projektleiter}
              setProjektleiter={setProjektleiter}
              projektleiterId={projektleiterId}
              setProjektleiterId={setProjektleiterId}
              managerTeamEntryId={managerTeamEntryId}
              setManagerTeamEntryId={setManagerTeamEntryId}
              projectStatusId={projectStatusId}
              onStatusChange={handleStatusChange}
              startDatum={startDatum}
              isEditingStartDate={isEditingStartDate}
              setIsEditingStartDate={setIsEditingStartDate}
              onStartDateChange={handleStartDateChange}
              endDatum={endDatum}
              isEditingEndDate={isEditingEndDate}
              setIsEditingEndDate={setIsEditingEndDate}
              onEndDateChange={handleEndDateChange}
              fortschritt={fortschritt}
              setFortschritt={setFortschritt}
              onProgressChange={handleProgressChange}
            />
          )}

          {activeTab === 'einstellungen' && (
            <RequirePermission permission={['manage_projects', 'manage_all_projects']}>
              <div className="p-4 pt-2 -mt-[8px] space-y-6">
                <div className="bg-[#DDDDDD] rounded-[24px] p-[32px] max-w-[1112px] w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* General Settings */}
                    <Card className="card-container-no-shadow card-container-border-black">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-xs font-medium text-slate-700">
                          {t('pages.projectDetails:settings.general.title')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">
                            {t('pages.projectDetails:settings.general.notifications')}
                          </label>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 border border-gray-200 rounded">
                              <span className="text-xs text-slate-700">
                                {t('pages.projectDetails:settings.general.emailNewTickets')}
                              </span>
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                defaultChecked={true}
                              />
                            </div>
                            <div className="flex items-center justify-between p-2 border border-gray-200 rounded">
                              <span className="text-xs text-slate-700">
                                {t('pages.projectDetails:settings.general.emailDueDates')}
                              </span>
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                defaultChecked={true}
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                  </div>
                </div>
              </div>
            </RequirePermission>
          )}

          {activeTab === 'team' && (
            <RequirePermission permission={['manage_projects', 'manage_all_projects', 'view_projects', 'view_all_projects']}>
              <ProjectTeamTab
                project={project}
                projectId={id!}
                searchTerm={globalTeamSearchTerm}
                canManageMembers={canManageTeamMembers}
                onAddMemberReady={(open) => {
                  openAddMemberRef.current = open;
                }}
                onDeleteSelectedReady={(removeSelected) => {
                  deleteSelectedTeamMembersRef.current = removeSelected;
                }}
                onSelectionChange={(count) => {
                  setTeamSelectionCount(count);
                }}
              />
            </RequirePermission>
          )}

          {activeTab === 'groups' && (
            <RequirePermission permission={['manage_projects', 'manage_all_projects', 'view_projects', 'view_all_projects']}>
              <GroupsTab
                project={project}
                projectId={id!}
                searchTerm={globalGroupSearchTerm}
                canManageGroups={canManageGroups}
                onCreateGroupReady={(open) => {
                  openCreateGroupRef.current = open;
                }}
                onDeleteSelectedReady={(removeSelected) => {
                  deleteSelectedGroupsRef.current = removeSelected;
                }}
                onSelectionChange={(count) => {
                  setGroupSelectionCount(count);
                }}
              />
            </RequirePermission>
          )}

          {activeTab === 'planLevel' && (
            <RequirePermission permission={['manage_projects', 'manage_all_projects', 'view_projects', 'view_all_projects']}>
              <React.Suspense fallback={<div className="p-6 flex items-center justify-center"><LogoLoader size="md" text="Loading plan view..." showText /></div>}>
                <ProjectPlanLevelTab
                  projectId={id!}
                  plans={plans}
                  tickets={tickets}
                  canManagePlan={canManagePlans}
                  selectedPlan={selectedPlan}
                  onPlanSelect={setSelectedPlan}
                  onPlanDelete={handlePlanDelete}
                  onPlanUpload={() => setShowPlanUpload(true)}
                  isDeletingPlan={isDeletingPlan}
                  onPinConfirmed={async (pinLocation) => {
                    if (isContractor || !canManagePlans) {
                      await alert({
                        message: t('common:accessDenied.message', 'You don\'t have permission to access this section'),
                        title: t('common:accessDenied.title', 'Access Denied'),
                      });
                      return;
                    }
                    setPendingPinLocation(pinLocation);
                    setShowNewTicketModal(true);
                  }}
                  onTicketClick={handleTicketClick}
                  onPinRelocated={async (ticketId, updates) => {
                    if (isContractor || !canManagePlans) {
                      await alert({
                        message: t('common:accessDenied.message', 'You don\'t have permission to access this section'),
                        title: t('common:accessDenied.title', 'Access Denied'),
                      });
                      return;
                    }

                    // Cancel any outgoing refetches to avoid overwriting optimistic update
                    await queryClient.cancelQueries({ queryKey: QUERY_KEYS.projectTickets(id) });

                    // Snapshot the previous value for rollback
                    const previousTickets = queryClient.getQueryData(QUERY_KEYS.projectTickets(id));

                    // Optimistically update the cache with new pin location
                    queryClient.setQueryData(QUERY_KEYS.projectTickets(id), (oldData: any) => {
                      if (!oldData?.data) return oldData;
                      return {
                        ...oldData,
                        data: oldData.data.map((ticket: any) =>
                          ticket.id === ticketId
                            ? { ...ticket, planLocation: updates.planLocation }
                            : ticket
                        )
                      };
                    });

                    try {
                      const response = await apiRequest(`/api/tickets/${ticketId}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(updates),
                      });
                      if (!response.ok) {
                        throw new Error('Failed to update ticket');
                      }
                      // Invalidate tickets to refresh with full server data (including preview image)
                      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTickets(id) });
                      toast({
                        title: t('tickets.pinRelocated', 'Pin Relocated'),
                        description: t('tickets.pinRelocatedSuccess', 'Ticket pin location has been updated'),
                      });
                    } catch (error) {
                      console.error('Failed to relocate pin:', error);
                      // Rollback to previous state on error
                      if (previousTickets) {
                        queryClient.setQueryData(QUERY_KEYS.projectTickets(id), previousTickets);
                      }
                      toast({
                        title: t('common:error', 'Error'),
                        description: t('tickets.pinRelocateError', 'Failed to update pin location'),
                        variant: 'destructive',
                      });
                    }
                  }}
                  planLimits={planLimits}
                  onCreateTicket={async () => {
                    if (isContractor || !canManagePlans) {
                      await alert({
                        message: t('common:accessDenied.message', 'You don\'t have permission to access this section'),
                        title: t('common:accessDenied.title', 'Access Denied'),
                      });
                      return;
                    }
                    setShowNewTicketModal(true);
                  }}
                  isLoadingPlans={isLoadingPlans}
                  onControlsReady={setPlanLevelMiddleControls}
                  onStartPinPlacementReady={(fn) => {
                    startPinPlacementRef.current = fn;
                  }}
                />
              </React.Suspense>
            </RequirePermission>
          )}

          {activeTab === 'tickets' && (
            <RequirePermission permission={['manage_projects', 'manage_all_projects', 'view_projects', 'view_all_projects']}>
              <ProjectTicketsTab
                projectId={id!}
                project={project}
                tickets={tickets}
                plans={plans}
                selectedPlanLevelId={ticketsTabPlanLevelId}
                teamMembers={teamMembers}
                statusList={statusList}
                priorities={priorities}
                gewerke={gewerke}
                isLoading={ticketsLoading}
                onOpenEditModal={(ticket) => {
                  setSelectedTicket(ticket);
                  updateTicketForm(ticket);
                  setShowInlineEditModal(true);
                }}
                onOpenNewTicketModal={() => setShowNewTicketModal(true)}
                onSelectionChange={(hasSelection, getSelectedData) => {
                  setTicketsTabHasSelection(hasSelection);
                  ticketsTabGetSelectedDataRef.current = getSelectedData;
                }}
                onTransformTicketsForExport={async (tickets, options) => {
                  return Promise.all(tickets.map(ticket => transformTicketForExportTicketsTab(ticket, options)));
                }}
              />
            </RequirePermission>
          )}

          {activeTab === 'documents' && (
            <RequirePermission permission={['manage_projects', 'manage_all_projects', 'view_projects', 'view_all_projects']}>
              <ProjectDocumentsTab
                documents={documents}
                projectId={id!}
                organizationId={organizationId}
                isLoading={isLoadingDocuments}
                searchTerm={globalDocumentsSearchTerm}
                canManageDocuments={canManageDocuments}
                onUploadClick={(handler) => {
                  documentsUploadHandlerRef.current = handler;
                }}
              />
            </RequirePermission>
          )}
        </div>

        {/* Hidden file input for plan upload */}
        <input
          ref={planFileInputRef}
          type="file"
          onChange={handlePlanFileUpload}
          className="hidden"
          accept=".pdf,.dwg,.jpg,.jpeg,.png"
        />

        {/* Plan Upload Modal */}
        {showPlanUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{t('form.uploadPlan')}</h2>
                <button
                  onClick={() => setShowPlanUpload(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.planName')}
                  </label>
                  <input
                    type="text"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder={t('form.enterPlanName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.planFile')}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setPlanFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowPlanUpload(false);
                      setPlanName('');
                      setPlanFile(null);
                    }}
                    disabled={isUploading}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    {t('form.cancel')}
                  </button>
                  <button
                    onClick={handlePlanUpload}
                    disabled={isUploading || !planName.trim() || !planFile}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? t('ui.uploading') : t('form.upload')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Inline Edit Modal (feature-toggle) */}
        {showInlineEditModal && selectedTicket && (
          <React.Suspense fallback={<div className="p-4 flex items-center justify-center"><LogoLoader size="md" text="Loading editor…" showText /></div>}>
            <EditTicketInlineModal
              isOpen={showInlineEditModal}
              onClose={() => { setShowInlineEditModal(false); setSelectedTicket(null); }}
              ticket={selectedTicket}
              planLocation={selectedTicket?.planLocation || null}
              projectId={id || undefined}
              projectName={project?.name} // Pass project name to avoid fetching all projects
              prefetchedPlans={plans}
              prefetchedPriorities={priorities}
              prefetchedStatus={statusList}
              prefetchedGewerke={gewerke}
              prefetchedTypen={typen}
              prefetchedTeamGroups={teamGroups}
              onUpdated={() => {
                // Refresh lists/stats after update
                queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTickets(id) });
                queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
                queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
              }}
            />
          </React.Suspense>
        )}

        {/* New Ticket Modal */}
        {showNewTicketModal && (
          <React.Suspense fallback={<div className="p-4 flex items-center justify-center"><LogoLoader size="md" text="Loading editor…" showText /></div>}>
            <EditTicketInlineModal
              isOpen={showNewTicketModal}
              onClose={() => {
                setShowNewTicketModal(false);
                setPendingPinLocation(null);
              }}
              ticket={null}
              projectId={id || undefined}
              projectName={project?.name} // Pass project name to avoid fetching all projects
              planLocation={pendingPinLocation || undefined}
              prefetchedPlans={plans}
              prefetchedPriorities={priorities}
              prefetchedStatus={statusList}
              prefetchedGewerke={gewerke}
              prefetchedTypen={typen}
              prefetchedTeamGroups={teamGroups}
              onUpdated={() => {
                queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTickets(id) });
              }}
            />
          </React.Suspense>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && ticketToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Delete Ticket
                </h3>
                <p className="text-sm text-slate-600 mb-6">
                  Are you sure you want to delete ticket <strong>#{ticketToDelete.ticketNumber}</strong> - <strong>{ticketToDelete.title}</strong>?
                  This action cannot be undone.
                </p>

                <div className="flex justify-center space-x-3">
                  <button
                    onClick={handleDeleteCancel}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded hover:bg-slate-50"
                    disabled={deleteTicketMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      deleteTicketMutation.mutate(ticketToDelete.id);
                    }}
                    disabled={deleteTicketMutation.isPending}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 font-medium"
                  >
                    {deleteTicketMutation.isPending ? (
                      <div className="flex items-center space-x-2">
                        <LogoLoader size="sm" />
                        <span>Deleting...</span>
                      </div>
                    ) : (
                      'Delete Ticket'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logo Crop Modal */}
        <LogoCropModal
          isOpen={showLogoCropModal}
          imageFile={selectedLogoFile}
          onClose={handleLogoCropCancel}
          onSave={handleLogoCropSave}
          title={t('pages.projectDetails:settings.logos.cropTitle') || 'Crop Logo'}
          instructions={t('pages.projectDetails:settings.logos.cropInstructions') || 'Drag to position and use zoom controls to adjust your logo. The circular area will be your logo.'}
        />
      </PageHeaderLayout>
    </AppShell>
  );
}
